import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import GuestClaimModal from "@/components/GuestClaimModal";
import { toast } from "sonner";
import { Sparkles, Copy, Mail, Loader2 } from "lucide-react";

// Dauerhafter, dezenter "Konto sichern"-Hinweis im Gastmodus (blockiert nie das Erkunden).
export default function GuestBanner() {
  const { user, logout, sendResumeEmail } = useAuth();
  const [claimOpen, setClaimOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitEmail, setExitEmail] = useState("");
  const [exiting, setExiting] = useState(false);

  // Globales Read-only-Signal (aus dem Axios-Interceptor): Write geblockt → Konto sichern
  useEffect(() => {
    const onReadonly = (e) => {
      const msg = e?.detail?.message || "Das ist eine Read-only-Demo. Sichere dein Konto, um eigene Daten zu erfassen.";
      toast.info(msg);
      setClaimOpen(true);
    };
    window.addEventListener("ivoliq:guest-readonly", onReadonly);
    return () => window.removeEventListener("ivoliq:guest-readonly", onReadonly);
  }, []);

  if (!user?.is_guest) return null;

  const exitDemo = async () => {
    await logout();
    // Harter Redirect auf die Landingpage — umgeht den ProtectedRoute-Rerender,
    // der sonst (kurz nach logout) auf /login umleiten würde.
    window.location.assign("/");
  };

  const exitWithEmail = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      const res = await sendResumeEmail(exitEmail.trim(), true);
      if (res && res.success === false) {
        toast.error(res.message || "E-Mail konnte gerade nicht gesendet werden.");
        setExiting(false);
        return;
      }
      toast.success("Wir haben dir deinen Demo-Link per E-Mail geschickt.");
    } catch {
      toast.error("E-Mail konnte nicht gesendet werden. Du kannst trotzdem verlassen.");
      setExiting(false);
      return;
    }
    await exitDemo();
  };

  const resumeLink = () => {
    let token = "";
    try { token = localStorage.getItem("ivoliq_demo_resume") || ""; } catch { /* ignore */ }
    return token ? `${window.location.origin}/demo/fortsetzen/${token}` : "";
  };

  const copyResumeLink = async () => {
    const link = resumeLink();
    if (!link) { toast.error("Demo-Link nicht verfügbar. Bitte starte die Demo neu."); return; }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Demo-Link kopiert – damit setzt du die Demo jederzeit fort.");
    } catch {
      toast.info(link);
    }
  };

  const emailResumeLink = async () => {
    if (sending) return;
    setSending(true);
    try {
      const res = await sendResumeEmail(email.trim());
      if (res && res.success === false) {
        toast.error(res.message || "E-Mail konnte gerade nicht gesendet werden. Bitte kopiere den Link.");
        return;
      }
      toast.success("Wir haben dir den Demo-Link per E-Mail geschickt.");
      setLinkOpen(false);
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "E-Mail konnte nicht gesendet werden. Bitte versuche es erneut.";
      toast.error(typeof msg === "string" ? msg : "E-Mail konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div
        className="sticky top-0 z-40 w-full bg-emerald-900 text-emerald-50"
        data-testid="guest-banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm min-w-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-700/60 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide shrink-0">
              <Sparkles className="h-3 w-3" /> Demo
            </span>
            <span className="hidden md:inline truncate">
              Du testest ivoliq mit Beispieldaten — bitte keine echten personenbezogenen Daten eingeben. Gast-Daten werden nach 3 Tagen automatisch gelöscht.
            </span>
            <span className="md:hidden">Demo — Daten nach 3 Tagen gelöscht</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setLinkOpen(true)}
              className="hidden sm:inline text-sm text-emerald-100/90 hover:text-white underline underline-offset-2"
              data-testid="guest-banner-resume-link"
            >
              Demo-Link
            </button>
            <button
              onClick={() => setExitOpen(true)}
              className="text-sm text-emerald-100/90 hover:text-white underline underline-offset-2"
              data-testid="guest-banner-exit-btn"
            >
              Demo verlassen
            </button>
            <Button
              size="sm"
              onClick={() => setClaimOpen(true)}
              className="bg-white text-emerald-800 hover:bg-emerald-50"
              data-testid="guest-banner-save-btn"
            >
              Konto sichern
            </Button>
          </div>
        </div>
      </div>

      {/* Demo-Link: kopieren oder per E-Mail schicken (Rückkehr-Magic-Link) */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="sm:max-w-md" data-testid="guest-resume-link-dialog">
          <DialogHeader>
            <DialogTitle>Demo später fortsetzen</DialogTitle>
            <DialogDescription>
              Mit deinem persönlichen Link kommst du jederzeit – auch auf einem anderen Gerät – in genau diese Demo zurück.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Button
              variant="outline"
              onClick={copyResumeLink}
              className="w-full justify-center"
              data-testid="guest-resume-copy-btn"
            >
              <Copy className="h-4 w-4 mr-2" /> Link kopieren
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-2 text-xs text-slate-400">oder per E-Mail</span></div>
            </div>

            <div className="flex flex-col gap-2">
              <Input
                type="email"
                placeholder="deine@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="guest-resume-email-input"
              />
              <Button
                onClick={emailResumeLink}
                disabled={sending || !email.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-full justify-center"
                data-testid="guest-resume-email-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Link per E-Mail schicken
              </Button>
              <p className="text-xs text-slate-400">
                Wir nutzen deine E-Mail nur, um dir diesen Link zu senden.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GuestClaimModal open={claimOpen} onOpenChange={setClaimOpen} />

      {/* Demo verlassen: optional Rückkehr-Link per E-Mail schicken */}
      <Dialog open={exitOpen} onOpenChange={(o) => { if (!exiting) setExitOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="guest-exit-dialog">
          <DialogHeader>
            <DialogTitle>Demo verlassen?</DialogTitle>
            <DialogDescription>
              Möchtest du dir vorher deinen Rückkehr-Link per E-Mail schicken lassen? Dann kannst du
              die Demo jederzeit fortsetzen. (Optional – du kannst auch ohne verlassen.)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              type="email"
              placeholder="deine@email.de"
              value={exitEmail}
              onChange={(e) => setExitEmail(e.target.value)}
              data-testid="guest-exit-email-input"
            />
            <Button
              onClick={exitWithEmail}
              disabled={exiting || !exitEmail.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white w-full justify-center"
              data-testid="guest-exit-email-btn"
            >
              {exiting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Link schicken &amp; verlassen
            </Button>
            <Button
              variant="ghost"
              onClick={exitDemo}
              disabled={exiting}
              className="w-full justify-center text-slate-500 hover:text-slate-700"
              data-testid="guest-exit-plain-btn"
            >
              Ohne E-Mail verlassen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
